import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    List,
    ListItem,
    ListItemText,
    Typography,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@mui/material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';

interface Reseller {
    id: number;
    resellerName: string;
    emailId?: string;
    segpoolAccountId?: string;
}

function Reseller() {
    const [resellers, setResellers] = useState<Reseller[]>([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [newResellerName, setNewResellerName] = useState('');
    const [emailId, setEmailId] = useState('');
    const [segpoolAccountId, setSegpoolAccountId] = useState('');
    const [saving, setSaving] = useState(false);
    const [invitingId, setInvitingId] = useState<number | null>(null);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [inviteLinkData, setInviteLinkData] = useState<{ open: boolean; link: string; resellerName: string }>({ open: false, link: '', resellerName: '' });
    

    useEffect(() => {
        fetchResellers()
    }, []);

    const fetchResellers=()=>{
        axios.get(API_PATH + "/customer_ledger/allResellers", getAxiosConfig())
            .then((resellerRes) => {
                setResellers(resellerRes.data);
            })
            .catch((error) => {
                console.error("Error fetching reseller data:", error);
            });
    }

    // Add/Edit reseller
    const handleSave = async () => {
        if (!newResellerName.trim()) return;
        setSaving(true);
        try {
            await axios.post(API_PATH + '/customer_ledger/addReseller', { 
                id: editingId,
                resellerName: newResellerName.trim(),
                emailId: emailId.trim() || null,
                segpoolAccountId: segpoolAccountId.trim() || null
            }, getAxiosConfig());
            handleCloseModal();
            fetchResellers(); 
        } catch (err) {
            console.error('Failed to save reseller:', err);
        } finally {
            setSaving(false);
        }
    };

    const openEditModal = (reseller: Reseller) => {
        setEditingId(reseller.id);
        setNewResellerName(reseller.resellerName);
        setEmailId(reseller.emailId || '');
        setSegpoolAccountId(reseller.segpoolAccountId || '');
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
        setEditingId(null);
        setNewResellerName('');
        setEmailId('');
        setSegpoolAccountId('');
    };

    const handleInvite = async (reseller: Reseller) => {
        setInvitingId(reseller.id);
        try {
            const res = await axios.post(API_PATH + `/customer_ledger/inviteReseller/${reseller.id}`, {}, getAxiosConfig());
            setInviteLinkData({ open: true, link: res.data, resellerName: reseller.resellerName });
        } catch (err: any) {
            console.error('Failed to invite reseller:', err);
            alert(err.response?.data?.message || err.response?.data || 'Failed to send invitation.');
        } finally {
            setInvitingId(null);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(inviteLinkData.link || '');
        alert("Link copied to clipboard!");
    };

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Reseller List</Typography>
                <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
                    Add Reseller
                </Button>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : (
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>ID</strong></TableCell>
                            <TableCell><strong>Reseller Name</strong></TableCell>
                            <TableCell><strong>Email</strong></TableCell>
                            <TableCell><strong>SegPool Account</strong></TableCell>
                            <TableCell><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {resellers.map((reseller) => (
                            <TableRow key={reseller.id}>
                                <TableCell>{reseller.id}</TableCell>
                                <TableCell>{reseller.resellerName}</TableCell>
                                <TableCell>{reseller.emailId || '-'}</TableCell>
                                <TableCell>{reseller.segpoolAccountId || '-'}</TableCell>
                                <TableCell>
                                    <Button 
                                        variant="outlined" 
                                        color="primary" 
                                        size="small"
                                        disabled={!reseller.emailId || invitingId === reseller.id}
                                        onClick={() => handleInvite(reseller)}
                                        style={{ marginRight: 8 }}
                                    >
                                        {invitingId === reseller.id ? 'Inviting...' : 'Invite'}
                                    </Button>
                                    <Button 
                                        variant="outlined" 
                                        color="secondary" 
                                        size="small"
                                        onClick={() => openEditModal(reseller)}
                                    >
                                        Edit
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}


            <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="sm">
                <DialogTitle>{editingId ? 'Edit Reseller' : 'Add New Reseller'}</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Reseller Name"
                        fullWidth
                        margin="dense"
                        value={newResellerName}
                        onChange={(e) => setNewResellerName(e.target.value)}
                        autoFocus
                    />
                    <TextField
                        label="Email ID"
                        fullWidth
                        margin="dense"
                        value={emailId}
                        onChange={(e) => setEmailId(e.target.value)}
                    />
                    <TextField
                        label="SegPool Account ID"
                        fullWidth
                        margin="dense"
                        value={segpoolAccountId}
                        onChange={(e) => setSegpoolAccountId(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} disabled={saving}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={inviteLinkData.open} onClose={() => setInviteLinkData({ ...inviteLinkData, open: false })} fullWidth maxWidth="sm">
                <DialogTitle>Invitation Link - {inviteLinkData.resellerName}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        This link will expire in 3 days and can only be used once. Please send it securely to the reseller to setup their password.
                    </Typography>
                    <TextField
                        fullWidth
                        margin="dense"
                        value={inviteLinkData.link}
                        InputProps={{
                            readOnly: true,
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInviteLinkData({ ...inviteLinkData, open: false })}>Close</Button>
                    <Button onClick={handleCopy} variant="contained" color="primary">Copy Link</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Reseller;
